import React, { useEffect, useState, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import ERROR_CODE from '../../constants/error-codes';
import PERMISSIONS from '../../constants/permissions';
import ROUTE from '../../constants/route';
import {convertEditorStateToHtml} from '../../helpers/formik';
import StringHelpers from '../../helpers/string-helpers';
import {Permission, Role} from '../../helpers/user';
import PageLayout from '../../layouts/PageLayout';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import BuyCreditsDialog from '../../scenes/Dialog/components/BuyCreditsDialog';
import Form from '../../scenes/Form';
import ProcessCreateUpdateForm from '../../scenes/Form/components/ProcessCreateUpdateForm';
import Sidebar from '../../scenes/Sidebar';
import InBoundsHistory from '../../scenes/Sidebar/components/InBoundsHistory';
import JobCredits from '../../scenes/Sidebar/components/JobCredits';
import './styles.css';
import {get, post} from '../../services/api';

const CreateUpdateProcess = props => {
  const {
    user,
    location: { pathname },
    match: {
      params: { id = null, action, page },
    },
    history,
    pageTitle,
    globalActions: { updateUserPermission },
  } = props;

  const backPathname = useMemo(() => {
    return pathname.replace(
      action === 'create' ? `/${action}` : `/${id}/${action}`,
      ''
    );
  }, []);

  const endpointGet =
    page === 'process'
      ? ENDPOINT.FORM.GET.JOB
      : ENDPOINT.FORM.GET.JOB_SUBSCRIPTION;
  const endpointPost =
    page === 'process'
      ? ENDPOINT.FORM.SAVE.JOB
      : ENDPOINT.FORM.SAVE.JOB_SUBSCRIPTION;

  const initValues = {
    PositionTitle: '',
    AboutUs: '',
    Descriptions: '',
    AdditionalDetails: '',
    Hidden: false,
    AppDeadline: false,
    AppDeadlineDate: null,
    gatreq: false,
    recomreq: false,
    CoverLetterRequire: false,
    BlockLinkedInSharing: false,
    EmailTemplateFollowupID: null,
    ATSLink: null,
  };

  const isRestrictedFirm = useMemo(() => Role.isRecruiterRestricted(user), [
    user,
  ]);
  const showHiddenField = useMemo(
    () =>
      page === 'process'
        ? Permission.isAllowedCreateHiddenProcess(user.permissions)
        : false,
    [user]
  );

  const [formData, setFormData] = useState({
    isFetched: false,
    values: initValues,
    filters: {},
    credits: 0,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setFormData({
      isFetched: false,
      values: initValues,
      filters: {},
      credits: 0,
    });

    get(
      endpointGet,
      { id },
      {
        handleError: handleNotFound,
        handleSuccess,
      }
    );
  }, [action]);

  const handleClose = () => {
    if (action === 'create') {
      history.replace(backPathname);
    } else {
      history.push(backPathname);
    }
  };

  const handleSaveForm = (values, filters, { setSubmitting, setErrors }) => {
    const {
      ProcessID: id = null,
      PositionTitle,
      AboutUs,
      Descriptions,
      AdditionalDetails,
      Hidden,
      AppDeadline,
      AppDeadlineDate,
      gatreq,
      recomreq,
      CoverLetterRequire,
      BlockLinkedInSharing,
      EmailTemplateFollowupID,
      hasFollowUpEmail,
      startDate: { month, year },
      hasATSLink,
      ATSLink,
    } = values;

    const data = {
      PositionTitle,
      AboutUs: convertEditorStateToHtml(AboutUs),
      Descriptions: convertEditorStateToHtml(Descriptions),
      AdditionalDetails: convertEditorStateToHtml(AdditionalDetails),
      StartDateMonth: month,
      StartDateYear: year,
      AppDeadline: AppDeadline ? 'Yes' : 'No',
      gatreq: gatreq ? 'Yes' : 'No',
      recomreq: recomreq ? 'Yes' : 'No',
      Hidden: Hidden ? 'Yes' : 'No',
      CoverLetterRequire: CoverLetterRequire ? 'Yes' : 'No',
      BlockLinkedInSharing: BlockLinkedInSharing ? 'Yes' : 'No',
      EmailTemplateFollowupID:
        hasFollowUpEmail &&
        EmailTemplateFollowupID &&
        EmailTemplateFollowupID.value
          ? EmailTemplateFollowupID.value
          : '',
      ATSLink: hasATSLink && ATSLink ? ATSLink : '',
      ...filters,
    };

    if (id) {
      data.id = id;
    }

    if (AppDeadline && AppDeadlineDate) {
      data.AppDeadlineDate =
        StringHelpers.appendLeadingZeroes(AppDeadlineDate.getMonth() + 1) +
        '/' +
        StringHelpers.appendLeadingZeroes(AppDeadlineDate.getDate()) +
        '/' +
        AppDeadlineDate.getFullYear();
    }

    post(endpointPost, data, {
      handleErrorCode: response => {
        setSubmitting(false);
        handleErrorCode(response);
      },
      handleForbidden: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: () => {
        if (action === 'create') {
          updateUserPermission(PERMISSIONS.POST_JOB);
        }
        history.push(backPathname);
      },
    });
  };

  const handleNotFound = () => {
    history.replace(ROUTE.ERROR_404);
  };

  const handleErrorCode = ({ status, data: { code } }) => {
    if (code === ERROR_CODE.NO_CREDITS) {
      BuyCreditsDialog({ history });
    }
  };

  const handleError = ({ status, data: { fields, message } }, setErrors) => {
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        if (key === 'startDateYearField') {
          obj.startDate = `${value}`;
        } else {
          obj[key] = `${value}`;
        }
      }
      setErrors(obj);
    } else {
      BasicDialog({ bodyRender: message || status });
    }
  };

  const handleSuccess = data => {
    const { filters, fields = {}, credits = 0 } = data;
    setFormData({
      isFetched: true,
      filters,
      values: { ...initValues, ...fields },
      credits: credits,
    });
  };

  const { values, filters, isFetched, credits } = formData;

  return (
    <PageLayout
      pageClass="add-process"
      pageTitle={pageTitle}
      mainContent={
        <Form>
          <ProcessCreateUpdateForm
            title={action === 'create' ? 'Search Request' : 'Edit Search'}
            isSubscriptionProcess={page !== 'process'}
            values={values}
            filters={filters}
            isFetched={isFetched}
            hasFieldHidden={showHiddenField}
            onSaveFormHandler={handleSaveForm}
            onCloseFormHandler={handleClose}
          />
        </Form>
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Sidebar>
            {isRestrictedFirm && <JobCredits creditsCount={credits} />}
            <InBoundsHistory isRecruiter />
          </Sidebar>
        </CSSTransition>
      }
    />
  );
};

export default CreateUpdateProcess;
